import { sum } from 'lodash-es'
import { useMemo, useState } from 'react'

import { FvButton, FvNum, ValidatedForm } from '@fv/client-components'
import {
  applyTaxes,
  generateCharge,
  getAmount,
  isManagedTax,
  modelToDto,
  switchFromCharges,
  switchToCharges,
} from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'
import type { ChargeName, Currency, QuoteDTO } from '@fv/models'
import { chargeNameLabel, quoteUtils } from '@fv/models/core'

import { useUserMarkup } from '@/hooks/settings/useUserSettings'
import {
  composeCityState,
  getDestination,
  getOrigin,
} from '@/utils/shipmentFuncs'

import {
  createQuoteEditModel,
  type QuoteEditModel,
} from '../mini-quote-list/types'
import { useUpdateQuote } from '../quote/useUpdateQuote'
import { BaseRateField } from './BaseRateField'
import { ChargeField } from './ChargeField'
import type { ChargeListState } from './types'

type Props = {
  chargeState: ChargeListState
  load: ListShipment
  onCancel: () => void
}

const READONLY_CHARGES: ChargeName[] = ['linehaul', 'freightview markup']

const processQuoteForMarkup = <
  T extends Pick<
    QuoteDTO,
    'amount' | 'charges' | 'source' | 'status' | 'method' | 'hasMarkup'
  >,
>(
  quote: T,
): T => {
  return {
    ...quote,
    hasMarkup: false,
    amount: quote.charges?.length
      ? sum(quote.charges.map(c => c.amount))
      : quote.amount,
  }
}

export const ChargeForm = ({ chargeState, load, onCancel }: Props) => {
  const model = createQuoteEditModel(chargeState.quote)

  const userMarkup = useUserMarkup()
  const [localQuote, setLocalQuote] = useState<QuoteEditModel>(
    quoteUtils.removeMarkup(model),
  )
  const markup = userMarkup?.[load.workflow]
  const localQuoteWithMarkup = useMemo(() => {
    return quoteUtils.applyMarkup(
      processQuoteForMarkup({ ...chargeState.quote, ...localQuote }),
      markup,
      load.exchangeRates,
    )
  }, [chargeState.quote, localQuote, markup, load.exchangeRates])
  const updateQuote = useUpdateQuote(load.loadId, chargeState.quote._id)

  const handleUpdate = (update: QuoteEditModel) => {
    const updated = applyTaxes(update, load.locations)
    const hasTaxCharges = updated.charges?.some(c =>
      isManagedTax(c.name, updated.currency, load.locations),
    )
    const needsSwitched =
      localQuote.charges?.length > 1 && (updated.charges?.length ?? 0) <= 1

    setLocalQuote(
      needsSwitched && !hasTaxCharges ? switchFromCharges(updated) : updated,
    )
  }

  const handleAddClick = () => {
    const hasNonTaxCharges = localQuote.charges?.some(
      c => !isManagedTax(c.name, localQuote.currency, load.locations),
    )
    const needsSwitched = !hasNonTaxCharges

    setLocalQuote(prev => {
      return needsSwitched
        ? switchToCharges(prev)
        : {
            ...prev,
            charges: [...(prev.charges ?? []), generateCharge()],
          }
    })
  }

  const handleSubmit = () => {
    updateQuote.mutate(modelToDto(localQuote))
    onCancel()
  }

  const origin = getOrigin(load)
  const destination = getDestination(load)

  return (
    <ValidatedForm className="flex flex-col h-full" onSubmit={handleSubmit}>
      <div className="overflow-auto">
        <p className="">
          Edit charges from {chargeState.quote.providerName} <br />
          {origin.company} {composeCityState(origin)} {origin.postalCode} to{' '}
          {destination.company} {composeCityState(destination)}{' '}
          {destination.postalCode}
        </p>

        <div className="bg-fv-blue-50 border border-fv-blue-200 p-4 mb-6">
          <div className="flex flex-col gap-y-2 mb-4">
            <BaseRateField
              quote={localQuote}
              onChange={handleUpdate}
              locations={load.locations}
            />
            {localQuote.charges
              ?.filter(
                c =>
                  !READONLY_CHARGES.includes(c.name) &&
                  !isManagedTax(c.name, localQuote.currency, load.locations),
              )
              .map((charge, ix) => (
                <ChargeField
                  key={ix}
                  charge={charge}
                  currency={localQuote.currency}
                  locations={load.locations}
                  onChange={newCharge =>
                    handleUpdate({
                      ...localQuote,
                      charges: localQuote.charges?.map(c =>
                        c.id === newCharge.id ? newCharge : c,
                      ),
                    })
                  }
                  onRemove={() =>
                    handleUpdate({
                      ...localQuote,
                      charges: localQuote.charges?.filter(
                        c => c.id !== charge.id,
                      ),
                    })
                  }
                />
              ))}
          </div>
          <div className="bg-fv-blue-25 border-fv-blue-200 border-dotted border-t -mx-4 px-4 py-3 -mb-4">
            {!!markup && (
              <div className="flex justify-end mb-3">
                <ChargeFooterDisplay
                  label="Markup"
                  amount={
                    localQuoteWithMarkup.charges?.find(
                      c => c.name === 'freightview markup',
                    )?.amount || 0
                  }
                  currency={localQuote.currency}
                />
              </div>
            )}
            {localQuote.charges
              ?.filter(c =>
                isManagedTax(c.name, localQuote.currency, load.locations),
              )
              .map(c => (
                <div key={c.id} className="flex justify-end mb-2">
                  <ChargeFooterDisplay
                    label={`${chargeNameLabel(c.name)} =`}
                    amount={c.amount}
                    currency={localQuote.currency}
                  />
                </div>
              ))}
            <div className="flex justify-between">
              <FvButton icon="plus" onClick={handleAddClick}>
                Add an additional charge
              </FvButton>
              <ChargeFooterDisplay
                label="Total price ="
                amount={getAmount(localQuoteWithMarkup)}
                currency={localQuote.currency}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-fv-blue-150 py-3 px-6 border-t mt-auto border-fv-blue-300 -mx-6 -mb-6 flex gap-x-3 justify-end">
        <FvButton icon="times" onClick={onCancel}>
          Cancel
        </FvButton>
        <FvButton icon="check" theme="primary" type="submit">
          Update price / Save charges
        </FvButton>
      </div>
    </ValidatedForm>
  )
}

type FooterDisplayProps = {
  label: string
  amount: number
  currency?: Currency
}
const ChargeFooterDisplay = ({
  label,
  amount,
  currency,
}: FooterDisplayProps) => {
  return (
    <div className="flex items-center gap-x-2">
      <div>{label}</div>
      <div className="bg-fv-beer-xlight border font-mono border-fv-beer-medium p-2 w-36 text-right flex items-start justify-end gap-x-1">
        <FvNum val={amount} currency={currency} includeEmptyDecimals />
      </div>
    </div>
  )
}
